import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import '../../styles/ui.css';

const AssignProposal = () => {
  const { proposalId } = useParams();
  const navigate = useNavigate();
  const [reviewers, setReviewers] = useState([]);
  const [assignedReviewers, setAssignedReviewers] = useState([]);
  const [selectedReviewers, setSelectedReviewers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000/api';
  const auth = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    await fetchReviewers();
    await fetchAssignedReviewers();
  };

  const fetchReviewers = async () => {
    try {
      const res = await fetch(
        `${API_BASE_URL}/assign-proposal/reviewers?ts=${Date.now()}`,
        { headers: { ...auth, 'Cache-Control': 'no-cache' }, cache: 'no-store' }
      );
      if (!res.ok) throw new Error('Failed to fetch reviewers');
      const data = await res.json();
      setReviewers(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching reviewers', err);
    }
  };

  const fetchAssignedReviewers = async () => {
    try {
      const res = await fetch(
        `${API_BASE_URL}/assign-proposal/assigned/${proposalId}?ts=${Date.now()}`,
        { headers: { ...auth, 'Cache-Control': 'no-cache' }, cache: 'no-store' }
      );
      if (!res.ok) {
        setAssignedReviewers([]);
        return;
      }
      const data = await res.json();

      const arr = Array.isArray(data) ? data : [];
      setAssignedReviewers(
        arr.map(r => ({
          id: r.id ?? r.reviewer_id,
          first_name: r.first_name,
          last_name: r.last_name,
          email: r.email,
          assigned_at: r.assigned_at,
        }))
      );
    } catch (err) {
      console.error('Error fetching assigned reviewers', err);
      setAssignedReviewers([]);
    }
  };

  const handleToggleReviewer = (id) => {
    setErrorMsg('');
    setSuccessMsg('');
    setSelectedReviewers((prev) =>
      prev.includes(id) ? prev.filter((rid) => rid !== id) : [...prev, id]
    );
  };

  const handleUnassign = async (reviewerId) => {
    try {
      const res = await fetch(`${API_BASE_URL}/assign-proposal/${proposalId}/${reviewerId}`, {
        method: 'DELETE',
        headers: auth,
      });

      const result = await res.json();
      if (res.ok) {
        setSuccessMsg('Reviewer unassigned.');
        await fetchAllData();
      } else {
        setErrorMsg(result.error || 'Failed to unassign reviewer.');
      }
    } catch (err) {
      console.error('Error unassigning reviewer', err);
      setErrorMsg('Failed to unassign reviewer.');
    }
  };

  const handleSubmit = async () => {
    const allReviewerIds = [
      ...assignedReviewers.map((r) => r.id),
      ...selectedReviewers,
    ];
    const uniqueReviewerIds = Array.from(new Set(allReviewerIds));

    if (uniqueReviewerIds.length === 0 || uniqueReviewerIds.length > 3) {
      setErrorMsg('Select between 1 and 3 reviewers.');
      return;
    }

    try {
      setLoading(true);
      const res = await fetch(`${API_BASE_URL}/assign-proposal/${proposalId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...auth,
        },
        body: JSON.stringify({ reviewerIds: uniqueReviewerIds }),
      });

      const result = await res.json();
      if (res.ok) {
        setSuccessMsg('Reviewers updated successfully!');
        setSelectedReviewers([]);
        await fetchAllData();
        setTimeout(() => navigate('/proposal/view-submissions'), 1200);
      } else {
        setErrorMsg(result.error || 'Failed to update reviewers.');
      }
    } catch (err) {
      console.error('Error saving assignment', err);
      setErrorMsg('Failed to update reviewers.');
    } finally {
      setLoading(false);
    }
  };

  const totalSelected = new Set([
    ...assignedReviewers.map((r) => r.id),
    ...selectedReviewers,
  ]).size;

  const saveDisabled = loading || totalSelected === 0 || totalSelected > 3;

  return (
    <div className="proposal-details-container">
      <div className="card">
        <div className="details-header">
          <h2>Assign Reviewers</h2>
          <span className="status-pill-header">Selected {totalSelected}/3</span>
        </div>
        <p className="section-subtitle">
          Choose <strong>1 to 3</strong> reviewers. Reviewers with 5 active assignments are unavailable.
        </p>

        {errorMsg && <div className="error-text">{errorMsg}</div>}
        {successMsg && <span className="document-status accepted">{successMsg}</span>}

        {assignedReviewers.length > 0 && (
          <div className="card" style={{ marginTop: 12 }}>
            <div className="section-title">Currently Assigned Reviewers</div>
            <div className="details-grid">
              {assignedReviewers.map((r) => (
                <div key={r.id} className="document-tile" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <div className="value" style={{ flex: 1 }}>
                    {r.first_name} {r.last_name} ({r.email})
                  </div>
                  <button className="btn" onClick={() => handleUnassign(r.id)}>
                    Unassign
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="card" style={{ marginTop: 12 }}>
          <table className="table-std">
            <thead>
              <tr>
                <th style={{ width: 48 }}></th>
                <th>Reviewer Name</th>
                <th>Email</th>
                <th>Active Proposals</th>
              </tr>
            </thead>
            <tbody>
              {reviewers.map((r) => {
                const unavailable = r.active_assignments >= 5;
                return (
                  <tr key={r.id}>
                    <td style={{ textAlign: 'center' }}>
                      <input
                        type="checkbox"
                        disabled={unavailable}
                        checked={selectedReviewers.includes(r.id)}
                        onChange={() => handleToggleReviewer(r.id)}
                      />
                    </td>
                    <td>{r.first_name} {r.last_name}</td>
                    <td>{r.email}</td>
                    <td>{r.active_assignments}</td>
                  </tr>
                );
              })}
              {reviewers.length === 0 && (
                <tr>
                  <td colSpan={4} className="empty-state">No reviewers found.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="actions-grid" style={{ marginTop: 12 }}>
          <button
            className="btn primary"
            onClick={handleSubmit}
            disabled={saveDisabled}
          >
            {loading ? 'Saving…' : 'Save'}
          </button>
          <button className="btn ghost" onClick={() => navigate(-1)}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default AssignProposal;
